import { forwardRef, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { StatusDot, type DotVariant } from '@/design-system/components/primitives/status-dot'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/design-system/components/primitives/tooltip'
import { ROLE_TO_VARIANT, type StatusRole } from '@/design-system/tokens/status'

/**
 * StatCard — счётчик-светофор «метка + число» (`.stat` макета). Цветовая статусная метка ссылается
 * на семантическую роль; статус сопровождается текстовой меткой (не только цвет). Число «нет данных»
 * использует вторичный текст. Адаптив набора 4→2 — у StatGrid, не здесь.
 *
 * Одной строкой «● Просрочено: 3» и минимальной высоты — текст и немного воздуха сверху и снизу
 * (ответ заказчицы 04.11, п.10: Главная, Обучение, Спецоценка; так же везде, где есть счётчики).
 */
export interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  value: string | number
  /**
   * Подсказка к названию (centrika-docs!33): краткая подпись окна, при наведении на неё —
   * полное наименование показателя. Без этого свойства подпись остаётся обычным текстом.
   */
  hint?: string
  /** Семантическая роль статуса метки. */
  status?: Exclude<StatusRole, 'info'>
  /** Либо визуальный вариант точки напрямую (примитивный API). */
  variant?: DotVariant
  /** Число в состоянии «нет данных» — вторичным цветом. */
  noData?: boolean
}

/**
 * Цвет числа = статус-токен (как inline-color в макете: red/amber/green).
 * «Нет данных» и серый статус — вторичный текст (`--txt2` макета). Без статуса — основной текст.
 */
const NUM_COLOR: Record<DotVariant, string> = {
  red: 'text-red',
  amber: 'text-amber',
  green: 'text-green',
  gray: 'text-txt-secondary',
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, label, value, status, variant, noData, hint, ...props }, ref) => {
    const dotVariant = (status ? ROLE_TO_VARIANT[status] : variant) as DotVariant | undefined
    const numClass = noData ? 'text-txt-secondary' : dotVariant ? NUM_COLOR[dotVariant] : 'text-txt'
    return (
      <div
        ref={ref}
        className={cn(
          'ds-surface flex flex-wrap items-baseline gap-x-1.5 rounded-12 px-4 py-2',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-1.5 self-center text-13 text-txt-secondary">
          {/* Точка декоративна: статус озвучивается видимой подписью ниже (без дублирования). */}
          {dotVariant ? <StatusDot variant={dotVariant} label={label} aria-hidden /> : null}
          {/* С подсказкой подпись становится триггером тултипа (centrika-docs!33). Триггер —
              собственная кнопка Radix (без `asChild`): она фокусируема, поэтому подсказка
              открывается и с клавиатуры, а не только по наведению. Стили обнуляют кнопочный вид —
              подпись остаётся подписью. */}
          {/* Подпись и двоеточие — вплотную, без отступа строки. */}
          <span className="inline-flex items-baseline">
            {hint ? (
              <Tooltip>
                <TooltipTrigger className="ds-focus-ring cursor-help rounded-7 text-left">
                  {label}
                </TooltipTrigger>
                <TooltipContent>{hint}</TooltipContent>
              </Tooltip>
            ) : (
              <span>{label}</span>
            )}
            <span aria-hidden>:</span>
          </span>
        </div>
        <div className={cn('text-16 font-semibold tabular-nums', numClass)}>{value}</div>
      </div>
    )
  },
)
StatCard.displayName = 'StatCard'
